import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { Query } from '../../../generated/internal.graphql';
import type { VPResult } from '../../VPResult';
import { type InternalApiError, toApiErrors } from '../errors';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * The staff signal: "is the caller VenuePilot staff?".
 *
 * It mirrors the AppSchema `admin.tools => EDIT` permission the legacy Rails navigation reads, and
 * exists so the SPA can decide whether to render a staff-only control without asking for a staff
 * identity it does not need.
 *
 * The field is `Boolean!` and **never false**: a caller who is not staff is rejected with HTTP 401
 * before the query is parsed, or gets a top-level `UNAUTHORIZED` error with `data: null`. So
 * *absence* means not-staff, and this returns `false` rather than an error for it — a rejection is
 * the expected answer for most callers, not an incident.
 *
 * Note what "staff" means today: `InternalGraphqlController` gates the whole schema on superadmin,
 * so `staff: true` is unobservable to anyone who is not one. Callers gating a superadmin-only
 * action on this are relying on that. If the gate is ever widened to non-superadmin staff, those
 * call sites need revisiting — they would start offering actions whose mutation still fails.
 */

export const STAFF_QUERY = gql`
  query internalStaff {
    staff
  }
`;

export const fetchStaff = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<boolean, InternalApiError[]>> => {
  try {
    const { data } = await client.query<Query>({
      query: STAFF_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: "is this operator staff?" is a property of the operator, answered from
      // the session cookie. It is also asked on the account-restore path, before the app has
      // settled which account it is in.
      context: ACCOUNT_AGNOSTIC,
    });

    return { success: true, data: !!data?.staff };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
